import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  FileText,
  Network,
  CalendarDays,
  Clock,
  Banknote,
  Building2,
  LogOut,
  Search,
  Menu,
  X,
  Boxes,
  Megaphone,
  GraduationCap,
} from 'lucide-react'
import { getUser, logout } from '../auth'
import NotificationsBell from './NotificationsBell'

interface LayoutProps {
  children: ReactNode
}

const navGroups = [
  {
    label: 'Overview',
    items: [{ path: '/', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'People',
    items: [
      { path: '/employees', label: 'Employees', icon: Users },
      { path: '/org-units', label: 'Org Units', icon: Network },
    ],
  },
  {
    label: 'Time & Pay',
    items: [
      { path: '/attendance', label: 'Attendance', icon: Clock },
      { path: '/leave', label: 'Leave', icon: CalendarDays },
      { path: '/payroll', label: 'Payroll', icon: Banknote },
    ],
  },
  {
    label: 'Learning',
    items: [{ path: '/learning', label: 'Learning & LMS', icon: GraduationCap }],
  },
  {
    label: 'Workplace',
    items: [
      { path: '/intranet', label: 'Intranet', icon: Megaphone },
      { path: '/documents', label: 'Documents', icon: FileText },
    ],
  },
  {
    label: 'Organization',
    items: [{ path: '/company', label: 'Company', icon: Building2 }],
  },
]

/** Prefixes that should highlight a nav item beyond exact matches. */
const navAliases: Record<string, string> = {
  '/courses': '/learning',
  '/sessions': '/learning',
  '/employees': '/employees',
  '/org-units': '/org-units',
  '/intranet': '/intranet',
  '/documents': '/documents',
  '/payroll': '/payroll',
}

function activeNavPath(pathname: string): string {
  for (const [prefix, navPath] of Object.entries(navAliases)) {
    if (pathname.startsWith(prefix + '/') || pathname === prefix) return navPath
  }
  return pathname
}

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  '/': { title: 'Dashboard', subtitle: 'Your workspace at a glance' },
  '/employees': { title: 'Employees', subtitle: 'People directory and records' },
  '/employees/new': { title: 'Add Employee', subtitle: 'Create a new employee record' },
  '/documents': { title: 'Documents', subtitle: 'Files and shared resources' },
  '/documents/upload': { title: 'Upload Document', subtitle: 'Store a file in Garage S3' },
  '/org-units': { title: 'Org Units', subtitle: 'Organization structure' },
  '/org-units/new': { title: 'Add Org Unit', subtitle: 'Create a unit in the org tree' },
  '/leave': { title: 'Leave', subtitle: 'Time-off requests and approvals' },
  '/leave/request': { title: 'Request Leave', subtitle: 'Submit a time-off request for approval' },
  '/intranet': { title: 'Intranet', subtitle: 'Company news, announcements, and SOP library' },
  '/intranet/new': { title: 'New Post', subtitle: 'Publish news or an announcement' },
  '/learning': { title: 'Learning', subtitle: 'Courses, progress, and certifications' },
  '/courses/new': { title: 'New Course', subtitle: 'Create a course for the catalog' },
  '/search': { title: 'Search', subtitle: 'Across employees, documents, and leave' },
  '/profile': { title: 'My Profile', subtitle: 'Your identity and employee record' },
  '/attendance': { title: 'Attendance', subtitle: 'Check in and track your time' },
  '/payroll': { title: 'Payroll', subtitle: 'Payslips and compensation' },
  '/payroll/payslips/new': { title: 'New Payslip', subtitle: 'Create or upsert a payslip' },
  '/payroll/runs/new': { title: 'New Payroll Run', subtitle: 'Batch-generate draft payslips' },
  '/company': { title: 'Company', subtitle: 'Organization profile and work settings' },
  '/company/edit': { title: 'Company Profile', subtitle: 'Edit organization details and policies' },
}

/** Titles for dynamic (id-parameterized) routes. */
function dynamicTitle(pathname: string) {
  if (/^\/employees\/[^/]+\/edit$/.test(pathname)) return { title: 'Edit Employee', subtitle: 'Update the employee record' }
  if (/^\/employees\/[^/]+$/.test(pathname)) return { title: 'Employee Profile', subtitle: 'Record, history, and timeline' }
  if (/^\/org-units\/[^/]+\/edit$/.test(pathname)) return { title: 'Edit Org Unit', subtitle: 'Update the org unit' }
  if (/^\/intranet\/[^/]+\/edit$/.test(pathname)) return { title: 'Edit Post', subtitle: 'Update the announcement' }
  if (/^\/courses\/[^/]+\/quiz\/edit$/.test(pathname)) return { title: 'Quiz Builder', subtitle: 'Create or edit the course quiz' }
  if (/^\/courses\/[^/]+\/quiz$/.test(pathname)) return { title: 'Quiz', subtitle: 'Answer all questions and submit' }
  if (/^\/courses\/[^/]+\/content\/edit$/.test(pathname)) return { title: 'Content Builder', subtitle: 'Modules and lessons for this course' }
  if (/^\/courses\/[^/]+\/learn$/.test(pathname)) return { title: 'Course Player', subtitle: 'Work through the lessons' }
  if (/^\/courses\/[^/]+\/sessions\/new$/.test(pathname)) return { title: 'Schedule Session', subtitle: 'Plan a classroom or webinar session' }
  if (/^\/courses\/[^/]+\/assign$/.test(pathname)) return { title: 'Assign Course', subtitle: 'Mandatory training with due dates' }
  if (/^\/sessions\/[^/]+\/edit$/.test(pathname)) return { title: 'Edit Session', subtitle: 'Reschedule or update the session' }
  if (/^\/courses\/[^/]+\/edit$/.test(pathname)) return { title: 'Edit Course', subtitle: 'Update the course' }
  if (/^\/courses\/[^/]+$/.test(pathname)) return { title: 'Course', subtitle: 'Course details and your progress' }
  if (/^\/payroll\/payslips\/[^/]+\/edit$/.test(pathname)) return { title: 'Edit Payslip', subtitle: 'Update the payslip' }
  return undefined
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation()
  const user = getUser()

  const handleLogout = () => {
    onNavigate?.()
    logout()
  }

  return (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="flex items-center gap-3 px-6 pt-7 pb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-500/25">
          <Boxes size={20} strokeWidth={2.2} />
        </div>
        <div>
          <p className="text-[15px] font-bold leading-tight tracking-tight">EDP Portal</p>
          <p className="text-[11px] font-medium text-slate-400">Enterprise Digitalized Platform</p>
        </div>
      </div>

      {/* Nav — grouped by module */}
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-2">
        {navGroups.map((group) => (
          <div key={group.label}>
            <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              {group.label}
            </p>
            <div className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon
                const isActive = activeNavPath(location.pathname) === item.path
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onNavigate}
                    className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-brand-50 text-brand-700 shadow-sm ring-1 ring-brand-100'
                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <Icon
                      size={18}
                      className={isActive ? 'text-brand-600' : 'text-slate-400 group-hover:text-slate-600'}
                    />
                    {item.label}
                    {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-brand-500" />}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User card */}
      <div className="p-4">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-bold text-white">
            {user?.initials ?? '…'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{user?.name ?? 'Signed in'}</p>
            <p className="truncate text-xs text-slate-400">{user?.email || user?.username || ''}</p>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [topQuery, setTopQuery] = useState('')

  // Close the mobile drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Keep the topbar box in sync with the q param while on /search
  useEffect(() => {
    if (location.pathname === '/search') {
      setTopQuery(searchParams.get('q') ?? '')
    }
  }, [location.pathname, searchParams])

  const submitSearch = () => {
    const q = topQuery.trim()
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`)
  }

  const page = pageTitles[location.pathname] ?? dynamicTitle(location.pathname) ?? pageTitles['/']

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-slate-200 bg-white lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-2xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-4 top-4 rounded-lg p-2 text-slate-400 hover:bg-slate-100"
            >
              <X size={18} />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-4 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md sm:px-6">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
          >
            <Menu size={20} />
          </button>

          <div className="min-w-0">
            <h1 className="truncate text-[15px] font-bold tracking-tight">{page.title}</h1>
            <p className="hidden truncate text-xs text-slate-400 sm:block">{page.subtitle}</p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative hidden md:block">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={topQuery}
                onChange={(e) => setTopQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitSearch()
                }}
                placeholder="Search everything…"
                className="w-56 rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:w-72 focus:border-brand-300 focus:bg-white focus:ring-4 focus:ring-brand-100"
              />
            </div>
            <NotificationsBell />
            <div className="ml-1 hidden h-8 w-px bg-slate-200 sm:block" />
            <Link
              to="/profile"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-bold text-white transition-all hover:shadow-lg hover:brightness-110"
              title={`${getUser()?.name ?? ''} — My Profile`}
            >
              {getUser()?.initials ?? '…'}
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div key={location.pathname} className="mx-auto w-full max-w-6xl animate-fade-up px-4 py-6 sm:px-6 sm:py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
